import axios from 'axios';

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API });

api.interceptors.request.use((config) => {
  const t = localStorage.getItem('ea_token');
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

// Normalise FastAPI error payloads so `detail` is ALWAYS a safe string.
// (422 validation errors return detail as an array of {type, loc, msg, ...} objects
// which crash React if rendered directly.)
const detailToString = (detail) => {
  if (!detail) return '';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((d) => (d && d.msg) ? d.msg : (typeof d === 'string' ? d : JSON.stringify(d))).join('; ');
  if (typeof detail === 'object') return detail.msg || JSON.stringify(detail);
  return String(detail);
};
api.interceptors.response.use(
  (r) => r,
  (error) => {
    if (error && error.response && error.response.data && 'detail' in error.response.data) {
      error.response.data.detail = detailToString(error.response.data.detail);
    }
    return Promise.reject(error);
  }
);

// Read a File into a base64 payload for JSON upload (avoids multipart)
export const fileToPayload = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () =>
      resolve({ filename: file.name, content_type: file.type || 'application/octet-stream', data: reader.result });
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

// Compress + convert an image to a small JPEG payload (handles large phone/HEIC photos
// that mobile networks drop and browsers can't display).
export const imageToPayload = (file, maxDim = 1200, quality = 0.82) =>
  new Promise((resolve, reject) => {
    if (!file.type || !file.type.startsWith('image/')) { reject(new Error('Please choose an image file.')); return; }
    if (file.size > 25 * 1024 * 1024) { reject(new Error('Image is too large (max 25MB).')); return; }
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        if (width > maxDim || height > maxDim) {
          if (width >= height) { height = Math.round((height * maxDim) / width); width = maxDim; }
          else { width = Math.round((width * maxDim) / height); height = maxDim; }
        }
        const canvas = document.createElement('canvas');
        canvas.width = width; canvas.height = height;
        canvas.getContext('2d').drawImage(img, 0, 0, width, height);
        const data = canvas.toDataURL('image/jpeg', quality);
        const name = (file.name || 'photo').replace(/\.[^.]+$/, '') + '.jpg';
        resolve({ filename: name, content_type: 'image/jpeg', data });
      };
      img.onerror = reject;
      img.src = reader.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

// Build a file URL (documents need ?auth=token; public photos do not)
export const fileUrl = (fileId, { auth = false } = {}) => {
  const base = `${API}/files/${fileId}/download`;
  if (!auth) return base;
  const t = localStorage.getItem('ea_token');
  return t ? `${base}?auth=${t}` : base;
};

export default api;
